import { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { colors, getSessionDrawerChrome, sessionDrawerSelectMenuButtonSx, sessionDrawerSelectMenuPaperSx } from '@kangaroo/ui';

import type { CompareSessionGroups, SessionListItem } from '../../features/sessions/sessionList';

type SessionSelectMenuProps = {
  label: string;
  value: string | null;
  sessions?: SessionListItem[];
  sessionGroups?: CompareSessionGroups;
  onChange: (sessionId: string) => void;
  disabled?: boolean;
};

function SessionListEntry({
  session,
  selected,
  onSelect,
  isDark,
}: {
  session: SessionListItem;
  selected: boolean;
  onSelect: (sessionId: string) => void;
  isDark: boolean;
}) {
  const chrome = getSessionDrawerChrome(isDark);

  return (
    <ListItemButton
      selected={selected}
      onClick={() => onSelect(session.id)}
      sx={{
        py: 0.85,
        alignItems: 'flex-start',
        '&.Mui-selected': {
          bgcolor: `${colors.primary[500]}22`,
          '&:hover': { bgcolor: `${colors.primary[500]}30` },
        },
      }}
    >
      <ListItemText
        primary={session.title}
        secondary={session.subtitle}
        primaryTypographyProps={{
          sx: { fontSize: '0.82rem', fontWeight: 700, color: chrome.menuPrimaryText },
          noWrap: true,
        }}
        secondaryTypographyProps={{
          sx: { fontSize: '0.68rem', color: chrome.menuSecondaryText },
          noWrap: true,
        }}
      />
    </ListItemButton>
  );
}

export function SessionSelectMenu({
  label,
  value,
  sessions,
  sessionGroups,
  onChange,
  disabled = false,
}: SessionSelectMenuProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const chrome = getSessionDrawerChrome(isDark);
  const buttonId = useId();
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const flatSessions = sessions ?? [
    ...(sessionGroups?.matching ?? []),
    ...(sessionGroups?.other ?? []),
  ];
  const selectedSession = flatSessions.find((session) => session.id === value) ?? null;
  const normalizedQuery = query.trim().toLowerCase();
  const matchesQuery = (session: SessionListItem) => !normalizedQuery || [
    session.title,
    session.subtitle,
    session.driverName,
    session.trackName,
    session.kartNumber,
  ].some((field) => field?.toLowerCase().includes(normalizedQuery));
  const filteredSessions = useMemo(
    () => flatSessions.filter(matchesQuery),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [flatSessions, normalizedQuery],
  );
  const filteredMatching = sessionGroups?.matching.filter(matchesQuery) ?? [];
  const filteredOther = sessionGroups?.other.filter(matchesQuery) ?? [];
  const hasGroupedOptions =
    Boolean(sessionGroups)
    && sessionGroups!.matching.length > 0
    && sessionGroups!.other.length > 0;

  const handleSelect = (sessionId: string) => {
    onChange(sessionId);
    setOpen(false);
    setQuery('');
  };

  useEffect(() => {
    if (!open) {
      setQuery('');
      return undefined;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const subheaderSx = {
    lineHeight: 1.6,
    fontSize: '0.62rem',
    fontWeight: 800,
    letterSpacing: '0.06em',
    textTransform: 'uppercase' as const,
    color: chrome.menuSubheaderText,
    bgcolor: chrome.menuSubheaderBg,
  };

  return (
    <Box ref={rootRef} sx={{ position: 'relative' }}>
      <Button
        id={buttonId}
        fullWidth
        variant="outlined"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        onClick={() => setOpen((current) => !current)}
        endIcon={(
          <KeyboardArrowDownIcon
            sx={{
              fontSize: 18,
              color: chrome.menuSecondaryText,
              transform: open ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.15s ease',
            }}
          />
        )}
        sx={sessionDrawerSelectMenuButtonSx(isDark, open)}
      >
        {selectedSession ? (
          <Stack spacing={0.1} sx={{ minWidth: 0, alignItems: 'flex-start', textAlign: 'left' }}>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, lineHeight: 1.25, color: chrome.menuPrimaryText }} noWrap>
              {selectedSession.title}
            </Typography>
            <Typography sx={{ fontSize: '0.68rem', color: chrome.menuSecondaryText, lineHeight: 1.25 }} noWrap>
              {selectedSession.subtitle}
            </Typography>
          </Stack>
        ) : (
          <Typography sx={{ fontSize: '0.82rem', color: chrome.menuSecondaryText }}>{label}</Typography>
        )}
      </Button>

      {open ? (
        <Box
          id={listId}
          role="listbox"
          aria-labelledby={buttonId}
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 20,
            mt: 0.5,
            maxHeight: 320,
            overflowY: 'auto',
            ...sessionDrawerSelectMenuPaperSx(isDark),
          }}
        >
          <Box sx={{ position: 'sticky', top: 0, zIndex: 2, p: 0.75, bgcolor: chrome.menuSubheaderBg, borderBottom: `1px solid ${chrome.divider}` }}>
            <TextField
              autoFocus
              fullWidth
              size="small"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => event.stopPropagation()}
              placeholder="Search date, driver, circuit, or kart"
              inputProps={{ 'aria-label': 'Search sessions' }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchOutlinedIcon sx={{ fontSize: 17 }} /></InputAdornment>,
              }}
              sx={{ '& .MuiInputBase-root': { height: 34, fontSize: '0.72rem', bgcolor: isDark ? '#111827' : '#fff' } }}
            />
            <Typography sx={{ mt: 0.5, px: 0.25, color: chrome.menuSecondaryText, fontSize: '0.62rem' }}>
              {filteredSessions.length} of {flatSessions.length} loaded sessions
            </Typography>
          </Box>
          <List dense disablePadding sx={{ py: 0.5 }}>
            {hasGroupedOptions ? (
              <>
                {filteredMatching.length > 0 ? <ListSubheader sx={subheaderSx}>Same circuit</ListSubheader> : null}
                {filteredMatching.map((session) => (
                  <SessionListEntry
                    key={session.id}
                    session={session}
                    selected={session.id === value}
                    onSelect={handleSelect}
                    isDark={isDark}
                  />
                ))}
                {filteredOther.length > 0 ? <ListSubheader sx={subheaderSx}>Other dates and circuits</ListSubheader> : null}
                {filteredOther.map((session) => (
                  <SessionListEntry
                    key={session.id}
                    session={session}
                    selected={session.id === value}
                    onSelect={handleSelect}
                    isDark={isDark}
                  />
                ))}
              </>
            ) : (
              filteredSessions.map((session) => (
                <SessionListEntry
                  key={session.id}
                  session={session}
                  selected={session.id === value}
                  onSelect={handleSelect}
                  isDark={isDark}
                />
              ))
            )}
            {filteredSessions.length === 0 ? (
              <Typography sx={{ py: 2.5, px: 1.5, color: chrome.menuSecondaryText, fontSize: '0.72rem', textAlign: 'center' }}>No sessions match that search.</Typography>
            ) : null}
          </List>
        </Box>
      ) : null}
    </Box>
  );
}
